import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  RATE_CARD,
  COMPLEXITY_TIERS,
  DIFFICULTY_TIERS,
  DETAIL_TIERS,
  STYLE_TIERS,
  CGI_PROJECT_FACTOR,
  CURRENCIES,
  tierForBudget,
} from "./rateCard";
import { buildTopSheet, formatMoney } from "./pricing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Blank starter for a new line item ────────────────────────── */
function blankItem(category = "cgi3d") {
  const base = { id: crypto.randomUUID(), category };
  if (category === "cgi3d") return { ...base, projectType: "Feature", runtime: 90, hero: 2, secondary: 4, props: 8, environments: 6, soundDesign: 90, tier: "Standard" };
  if (category === "vfx") return { ...base, vfxTask: "wireRemoval", shots: 40, avgShotLength: 4, difficulty: "Medium" };
  if (category === "animatic") return { ...base, mode: "runtime", runtime: 3, panels: 0, scenes: 8, detail: "Rough" };
  if (category === "anim2d") return { ...base, runtime: 5, characters: 3, backgrounds: 6, style: "Broadcast" };
  if (category === "mocap") return { ...base, mode: "takes", takes: 40, seconds: 0, difficulty: "Medium" };
  return base;
}

/* ─── Small primitives ─────────────────────────────────────────── */
const F = ({ label, children, hint }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] block mb-1.5">{label}</span>
    {children}
    {hint && <span className="block mt-1 font-mono text-[10px] text-[var(--vw-text-mute)] opacity-70">{hint}</span>}
  </label>
);

const NumInput = ({ value, onChange, min = 0, testid }) => (
  <input
    type="number"
    inputMode="numeric"
    min={min}
    value={value}
    onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
    className="vw-input"
    data-testid={testid}
  />
);

const Select = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="vw-input" data-testid={testid}>
    {options.map((o) => (
      <option key={o.value ?? o} value={o.value ?? o} className="bg-[var(--vw-surface)]">{o.label ?? o}</option>
    ))}
  </select>
);

/* ─── Category-specific input forms ────────────────────────────── */
function CGIFields({ item, patch }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <F label="Project type"><Select value={item.projectType} onChange={(v) => patch({ projectType: v })} options={Object.keys(CGI_PROJECT_FACTOR)} testid="est-cgi-type" /></F>
      <F label="Complexity tier"><Select value={item.tier} onChange={(v) => patch({ tier: v })} options={Object.keys(COMPLEXITY_TIERS)} testid="est-cgi-tier" /></F>
      <F label="Runtime (minutes)"><NumInput value={item.runtime} onChange={(v) => patch({ runtime: v })} testid="est-cgi-runtime" /></F>
      <F label="Hero characters"><NumInput value={item.hero} onChange={(v) => patch({ hero: v })} testid="est-cgi-hero" /></F>
      <F label="Secondary characters"><NumInput value={item.secondary} onChange={(v) => patch({ secondary: v })} testid="est-cgi-secondary" /></F>
      <F label="Hero props"><NumInput value={item.props} onChange={(v) => patch({ props: v })} testid="est-cgi-props" /></F>
      <F label="Environments / sets"><NumInput value={item.environments} onChange={(v) => patch({ environments: v })} testid="est-cgi-env" /></F>
      <F label="Sound design (minutes)" hint="Usually matches runtime"><NumInput value={item.soundDesign} onChange={(v) => patch({ soundDesign: v })} testid="est-cgi-sound" /></F>
    </div>
  );
}

function VFXFields({ item, patch }) {
  const taskOptions = Object.entries(RATE_CARD.vfx.lineItems).map(([k]) => ({
    value: k,
    label: {
      wireRemoval: "Wire & rig removal",
      rotoscoping: "Rotoscoping",
      cleanupPaint: "Clean-up / paint-out",
      mattePainting: "Matte painting",
      cgIntegration: "CG integration",
      compositing: "Compositing",
    }[k] || k,
  }));
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <F label="Task"><Select value={item.vfxTask} onChange={(v) => patch({ vfxTask: v })} options={taskOptions} testid="est-vfx-task" /></F>
      <F label="Difficulty"><Select value={item.difficulty} onChange={(v) => patch({ difficulty: v })} options={Object.keys(DIFFICULTY_TIERS)} testid="est-vfx-diff" /></F>
      <F label="Number of shots"><NumInput value={item.shots} onChange={(v) => patch({ shots: v })} testid="est-vfx-shots" /></F>
      <F label="Avg shot length (sec)"><NumInput value={item.avgShotLength} onChange={(v) => patch({ avgShotLength: v })} testid="est-vfx-avg" /></F>
    </div>
  );
}

function AnimaticFields({ item, patch }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <F label="Measure by"><Select value={item.mode} onChange={(v) => patch({ mode: v })} options={[{ value: "runtime", label: "Runtime (minutes)" }, { value: "panels", label: "Panel count" }]} testid="est-anim-mode" /></F>
      <F label="Detail level"><Select value={item.detail} onChange={(v) => patch({ detail: v })} options={Object.keys(DETAIL_TIERS)} testid="est-anim-detail" /></F>
      {item.mode === "runtime"
        ? <F label="Runtime (minutes)"><NumInput value={item.runtime} onChange={(v) => patch({ runtime: v })} testid="est-anim-runtime" /></F>
        : <F label="Panels"><NumInput value={item.panels} onChange={(v) => patch({ panels: v })} testid="est-anim-panels" /></F>}
      <F label="Scenes"><NumInput value={item.scenes} onChange={(v) => patch({ scenes: v })} testid="est-anim-scenes" /></F>
    </div>
  );
}

function Anim2DFields({ item, patch }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <F label="Style tier"><Select value={item.style} onChange={(v) => patch({ style: v })} options={Object.keys(STYLE_TIERS)} testid="est-2d-style" /></F>
      <F label="Runtime (minutes)"><NumInput value={item.runtime} onChange={(v) => patch({ runtime: v })} testid="est-2d-runtime" /></F>
      <F label="Characters"><NumInput value={item.characters} onChange={(v) => patch({ characters: v })} testid="est-2d-char" /></F>
      <F label="Backgrounds"><NumInput value={item.backgrounds} onChange={(v) => patch({ backgrounds: v })} testid="est-2d-bg" /></F>
    </div>
  );
}

function MocapFields({ item, patch }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <F label="Measure by"><Select value={item.mode} onChange={(v) => patch({ mode: v })} options={[{ value: "takes", label: "Takes" }, { value: "seconds", label: "Total seconds" }]} testid="est-mocap-mode" /></F>
      <F label="Difficulty"><Select value={item.difficulty} onChange={(v) => patch({ difficulty: v })} options={Object.keys(DIFFICULTY_TIERS)} testid="est-mocap-diff" /></F>
      {item.mode === "takes"
        ? <F label="Number of takes"><NumInput value={item.takes} onChange={(v) => patch({ takes: v })} testid="est-mocap-takes" /></F>
        : <F label="Total seconds"><NumInput value={item.seconds} onChange={(v) => patch({ seconds: v })} testid="est-mocap-sec" /></F>}
    </div>
  );
}

const CATEGORY_LABELS = {
  cgi3d: "3D CGI Animation",
  vfx: "VFX (Live Action)",
  animatic: "Animatics / Previz",
  anim2d: "2D Animation",
  mocap: "Mocap Cleanup",
};

/* ═══════════════════════════════════════════════════════════════ */
export default function Estimator() {
  const [items, setItems] = useState([blankItem("cgi3d")]);
  const [currency, setCurrency] = useState("USD");
  const [contact, setContact] = useState({ name: "", email: "", company: "", projectName: "" });
  const [quoteState, setQuoteState] = useState({ open: false, sending: false, done: false, error: "" });

  const topSheet = useMemo(() => buildTopSheet(items), [items]);

  const patchItem = (id, patch) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const changeCategory = (id, category) => setItems((prev) => prev.map((it) => (it.id === id ? blankItem(category) : it)));
  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));
  const addItem = (category) => setItems((prev) => [...prev, blankItem(category)]);

  const canSubmit = topSheet.rows.length > 0;

  const submitQuote = async () => {
    setQuoteState((s) => ({ ...s, sending: true, error: "" }));
    try {
      await axios.post(`${API}/estimator/quote`, {
        contact,
        currency,
        items,
        top_sheet: {
          rows: topSheet.rows,
          per_category: topSheet.perCategory,
          subtotal_usd: topSheet.subtotalUsd,
          discount_lines: topSheet.discountLines,
          combined_discount_pct: topSheet.combinedDiscountPct,
          total_discount_usd: topSheet.totalDiscountUsd,
          grand_total_usd: topSheet.grandTotalUsd,
          baseline_total_usd: topSheet.baselineTotalUsd,
          savings_usd: topSheet.savingsUsd,
          savings_pct: topSheet.savingsPct,
        },
      }, { timeout: 20000 });
      setQuoteState({ open: true, sending: false, done: true, error: "" });
    } catch (err) {
      setQuoteState({ open: true, sending: false, done: false, error: err?.response?.data?.detail || "Could not send request. Please email kumar@vawworldstudios.com directly." });
    }
  };

  return (
    <div className="min-h-screen bg-[var(--vw-bg)] text-[var(--vw-text)]" data-testid="estimator-page">
      {/* Top bar */}
      <div className="border-b border-[var(--vw-border)] bg-[var(--vw-bg-soft)] sticky top-0 z-30" style={{ backdropFilter: "saturate(180%) blur(18px)" }}>
        <div className="max-w-[1560px] mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="est-back">
            <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
              <defs><linearGradient id="estLogo" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse"><stop offset="0%" stopColor="#00E5FF"/><stop offset="100%" stopColor="#2B6CFF"/></linearGradient></defs>
              <path d="M16 1.5 L30.5 16 L16 30.5 L1.5 16 Z" stroke="url(#estLogo)" strokeWidth="1.4" fill="rgba(0,229,255,0.04)"/>
              <path d="M8.5 10.5 L13.5 22 L15.4 22 L11 10.5 Z" fill="url(#estLogo)"/>
              <path d="M15.4 22 L17.3 22 L20.2 15.2 L23.1 22 L25 22 L20.2 10.5 L14.8 22 Z" fill="url(#estLogo)" opacity="0.85"/>
            </svg>
            <span className="font-display text-[15px] text-[var(--vw-text)]">VawWorld</span>
            <span className="mx-3 h-4 w-px bg-[var(--vw-border-strong)]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">Budget Estimator</span>
          </Link>
          <div className="flex items-center gap-3">
            <Select value={currency} onChange={setCurrency} options={CURRENCIES} testid="est-currency" />
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="vw-container pt-14 pb-8">
        <div className="vw-eyebrow mb-5">PRODUCTION BUDGET · TOP SHEET</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tight max-w-[22ch]">
          Build a shot-by-shot budget. See <span className="font-serif-italic text-[var(--vw-cyan)]">what you save</span>.
        </h1>
        <p className="mt-5 max-w-2xl text-[var(--vw-text-dim)] leading-relaxed">
          Mix categories, add line items, pick a complexity tier. Your top sheet updates live — with a clean savings-vs-market baseline you can hand to your greenlight committee.
        </p>
      </section>

      {/* Main grid */}
      <div className="vw-container pb-24 grid lg:grid-cols-[1.35fr_1fr] gap-8 lg:gap-10">
        {/* LEFT — inputs */}
        <div className="space-y-6" data-testid="est-inputs">
          {items.map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="border border-[var(--vw-border)] bg-[var(--vw-bg-soft)] p-5 md:p-6"
              data-testid={`est-item-${idx}`}
            >
              <div className="flex flex-wrap items-center gap-3 justify-between mb-5">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] w-8">{String(idx + 1).padStart(2, "0")}</span>
                  <div className="min-w-[220px]">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] block mb-1.5">Category</span>
                    <Select
                      value={item.category}
                      onChange={(v) => changeCategory(item.id, v)}
                      options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
                      testid={`est-cat-${idx}`}
                    />
                  </div>
                </div>
                {items.length > 1 && (
                  <button
                    onClick={() => removeItem(item.id)}
                    className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] hover:text-[var(--vw-orange)] border border-[var(--vw-border)] hover:border-[var(--vw-orange)] px-3 py-2 transition-colors"
                    data-testid={`est-remove-${idx}`}
                  >
                    Remove
                  </button>
                )}
              </div>

              {item.category === "cgi3d" && <CGIFields item={item} patch={(p) => patchItem(item.id, p)} />}
              {item.category === "vfx" && <VFXFields item={item} patch={(p) => patchItem(item.id, p)} />}
              {item.category === "animatic" && <AnimaticFields item={item} patch={(p) => patchItem(item.id, p)} />}
              {item.category === "anim2d" && <Anim2DFields item={item} patch={(p) => patchItem(item.id, p)} />}
              {item.category === "mocap" && <MocapFields item={item} patch={(p) => patchItem(item.id, p)} />}
            </motion.div>
          ))}

          <div className="flex flex-wrap gap-2">
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <button
                key={value}
                onClick={() => addItem(value)}
                className="vw-btn-ghost !py-2.5 !px-4 !text-[10px]"
                data-testid={`est-add-${value}`}
              >
                + {label}
              </button>
            ))}
          </div>
        </div>

        {/* RIGHT — Top sheet */}
        <aside className="lg:sticky lg:top-24 self-start" data-testid="est-topsheet">
          <div className="border border-[var(--vw-border-strong)] bg-[var(--vw-bg-soft)]">
            {/* Savings hero */}
            <div className="p-6 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(0,229,255,0.14), rgba(43,108,255,0.08))" }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] mb-2">YOU SAVE · TOP SHEET</div>
              <div className="flex items-end justify-between gap-4">
                <div>
                  <div className="font-display text-5xl md:text-6xl leading-none tracking-tight text-[var(--vw-text)]" data-testid="est-savings-pct">
                    {topSheet.savingsPct.toFixed(1)}<span className="text-[var(--vw-cyan)]">%</span>
                  </div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">vs market baseline</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl md:text-3xl text-[var(--vw-text)]" data-testid="est-savings-amount">{formatMoney(topSheet.savingsUsd, currency)}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">off your P&amp;L</div>
                </div>
              </div>
            </div>

            {/* Subtotal + discounts stack */}
            <div className="border-t border-[var(--vw-border)] p-4 space-y-2">
              <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.22em]">
                <span className="text-[var(--vw-text-mute)]">Subtotal</span>
                <span className="text-[var(--vw-text)]" data-testid="est-subtotal">{formatMoney(topSheet.subtotalUsd, currency)}</span>
              </div>
              {topSheet.discountLines.map((d) => (
                <div key={d.id} className="flex items-center justify-between font-mono text-[12px] tracking-[0.18em] uppercase" data-testid={`est-discount-${d.id}`}>
                  <span className="font-bold text-[var(--vw-cyan)]">
                    {d.label} — {(d.pct * 100).toFixed(0)}%
                  </span>
                  <span className="text-[var(--vw-cyan)]">− {formatMoney(d.amountUsd, currency)}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 border-t border-[var(--vw-border)]">
              <div className="p-4 border-r border-[var(--vw-border)]">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-1">Vawworld Total</div>
                <div className="font-display text-xl text-[var(--vw-cyan)]" data-testid="est-grand">{formatMoney(topSheet.grandTotalUsd, currency)}</div>
                <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.24em] text-[var(--vw-text-mute)]">
                  after {(topSheet.combinedDiscountPct * 100).toFixed(0)}% stacked discount
                </div>
              </div>
              <div className="p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-1">Market baseline</div>
                <div className="font-display text-xl text-[var(--vw-text)] line-through opacity-60" data-testid="est-baseline">{formatMoney(topSheet.baselineTotalUsd, currency)}</div>
              </div>
            </div>

            {/* Reference-quality reel — currently only wired for the
                3D CGI Animation category. Other categories will get their
                own reels later. */}
            {topSheet.rows.length === 0 ? (
              <div className="border-t border-[var(--vw-border)] p-6 font-mono text-[11px] uppercase tracking-[0.24em] text-[var(--vw-text-mute)] text-center">
                Add quantities on the left — your top sheet and a reference reel build live.
              </div>
            ) : topSheet.perCategory.cgi3d ? (
              <QualityReel grandTotalUsd={topSheet.perCategory.cgi3d.subtotal * (1 - topSheet.combinedDiscountPct)} />
            ) : null}

            {/* Actions */}
            <div className="border-t border-[var(--vw-border-strong)] p-4 space-y-3">
              <button
                onClick={() => setQuoteState({ open: true, sending: false, done: false, error: "" })}
                disabled={!canSubmit}
                className="w-full vw-btn-primary vw-btn-glow !justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="est-get-quote"
              >
                Get Detailed Quote · Book a Call
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
            </div>
          </div>

          <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--vw-text-mute)] leading-relaxed">
            Indicative top sheet. Final shot-count and cost model are locked on a 30-minute discovery call — no obligation, no marketing spam.
          </div>
        </aside>
      </div>

      {/* Quote modal */}
      <AnimatePresence>
        {quoteState.open && (
          <QuoteModal
            contact={contact}
            setContact={setContact}
            state={quoteState}
            onClose={() => setQuoteState({ open: false, sending: false, done: false, error: "" })}
            onSubmit={submitQuote}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function QuoteModal({ contact, setContact, state, onClose, onSubmit }) {
  const setF = (k, v) => setContact((c) => ({ ...c, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    if (!contact.name.trim() || !contact.email.trim() || !contact.company.trim()) return;
    onSubmit();
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="est-quote-modal">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-lg" onClick={onClose} />
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-[var(--vw-surface)] border border-[var(--vw-border-strong)]">
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-9 h-9 grid place-items-center bg-black/40 border border-[var(--vw-border-strong)] hover:border-[var(--vw-cyan)] hover:text-[var(--vw-cyan)] transition-colors" aria-label="Close" data-testid="est-quote-close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 6l12 12M6 18L18 6"/></svg>
        </button>

        {state.done ? (
          <div className="p-8 md:p-10">
            <div className="vw-eyebrow mb-4" style={{ color: "var(--vw-cyan)" }}>REQUEST SENT</div>
            <h3 className="font-display text-3xl leading-tight text-[var(--vw-text)] mb-4">
              Top sheet <span className="font-serif-italic text-[var(--vw-cyan)]">on its way</span>.
            </h3>
            <p className="text-[var(--vw-text-dim)] leading-relaxed">
              Thanks {contact.name.split(" ")[0]}. Our line producer will be in touch within one business day with a shot-count breakdown and a calendar hold for a 30-minute discovery call.
            </p>
            <button onClick={onClose} className="mt-6 vw-btn-ghost" data-testid="est-quote-done">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 md:p-10">
            <div className="vw-eyebrow mb-4">GET DETAILED QUOTE</div>
            <h3 className="font-display text-3xl leading-tight text-[var(--vw-text)] mb-3">
              We'll route this to our <span className="font-serif-italic text-[var(--vw-cyan)]">line producer</span>.
            </h3>
            <p className="text-sm text-[var(--vw-text-dim)] leading-relaxed mb-6">
              Your top sheet is included. We reply with a shot-count locked cost model on a 30-min call.
            </p>
            <div className="space-y-4">
              <label className="block"><span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] block mb-1.5">Full Name *</span>
                <input required value={contact.name} onChange={(e) => setF("name", e.target.value)} className="vw-input" data-testid="est-quote-name" placeholder="Your name" /></label>
              <label className="block"><span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] block mb-1.5">Executive Email *</span>
                <input required type="email" value={contact.email} onChange={(e) => setF("email", e.target.value)} className="vw-input" data-testid="est-quote-email" placeholder="you@studio.com" /></label>
              <label className="block"><span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] block mb-1.5">Studio / Company *</span>
                <input required value={contact.company} onChange={(e) => setF("company", e.target.value)} className="vw-input" data-testid="est-quote-company" placeholder="Your company" /></label>
              <label className="block"><span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] block mb-1.5">Project Name</span>
                <input value={contact.projectName} onChange={(e) => setF("projectName", e.target.value)} className="vw-input" data-testid="est-quote-project" placeholder="Working title" /></label>
            </div>
            {state.error && (
              <div className="mt-4 border border-[var(--vw-orange)]/40 bg-[color:rgba(255,138,71,0.06)] px-4 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--vw-orange)]" data-testid="est-quote-error">
                {state.error}
              </div>
            )}
            <div className="mt-6 flex items-center gap-3">
              <button type="submit" disabled={state.sending} className="vw-btn-primary vw-btn-glow" data-testid="est-quote-submit">
                {state.sending ? "Sending…" : "Send Top Sheet"}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
              <button type="button" onClick={onClose} className="vw-btn-ghost">Cancel</button>
            </div>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ─── Reference-quality reel card (public-facing) ─────────────── */
function QualityReel({ grandTotalUsd }) {
  const tier = tierForBudget(grandTotalUsd);
  return (
    <div className="border-t border-[var(--vw-border)]" data-testid="est-quality-reel">
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)]">Reference Reel · This Budget</div>
            <div className="font-display text-lg leading-tight text-[var(--vw-text)] mt-1" data-testid="est-quality-headline">
              Sample video for quality reference only
            </div>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] border border-[var(--vw-cyan)]/40 px-2.5 py-1 whitespace-nowrap">
            {tier.label}
          </span>
        </div>

        <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "16 / 9" }}>
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${tier.videoId}?rel=0&modestbranding=1`}
            title="Sample video for quality reference only"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 w-full h-full"
            data-testid={`est-quality-video-${tier.id}`}
          />
        </div>

        <div className="border-t border-[var(--vw-border)] pt-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] mb-1">
            What you can expect at this budget
          </div>
          <p className="text-[13px] text-[var(--vw-text)] leading-relaxed">
            Cinematic frames, hero-grade animation, and print-master sound — engineered at Vawworld's Mumbai and Bhopal floors. Detailed shot-count, look-dev references and delivery timeline are locked with your producer on the discovery call.
          </p>
        </div>
      </div>
    </div>
  );
}

